import type { OfferOption } from '@utils/backend-api/subscribe';
import { Reporter } from '@utils/backend-api/subscribe';

import type { ArticleProps } from './Blocks/RecommendedArticleBlock/interfaces';
import type {
  alignmentValues,
  backgroundColorValues,
  fontFamilyValues,
  fontSizeValues,
  gridValues,
  marginValues,
  paddingValues,
  verticalAlignmentValues,
} from './Blocks/constants';

export enum ArticleClusterType {
  Article = 'article',
  Project = 'project',
}

// Recursive type to handle arbitrary nesting of values
export type RecursiveValue =
  | string
  | boolean
  | number
  | null
  | PageTemplateJSON
  | PageTemplateJSON[]
  | RecursiveValue[]
  | { [key: string]: RecursiveValue };

export interface ArticleClusterProps {
  headline: string;
  items: {
    id: string;
    bylines: string;
    image: string;
    publishedAt: string;
    slug: string;
    title: string;
    type: ArticleClusterType;
  }[];
}

export interface PageTemplateJSON {
  children?: PageTemplateJSON[];
  config?: Record<string, RecursiveValue>;
  content: Record<string, RecursiveValue>;
  id: string;
  type: string;
}

export interface PageTemplateMetadata {
  page_analytics?: string;
}

export interface PageTemplateProps {
  articleClusters?: Record<string, ArticleClusterProps>;
  offers?: {
    annual_offer: OfferOption;
    monthly_offer: OfferOption;
    pro_offer: OfferOption;
    yp_offer: OfferOption;
  };
  recommendedArticles?: ArticleProps[];
  reporters?: Reporter[];
  singleOffer?: OfferOption;
  templateJSON: {
    blocks: PageTemplateJSON[];
  };
  templateMetadata: PageTemplateMetadata;
}

export interface WithAnalyticsWrapperProps {
  config: Record<string, RecursiveValue>;
  children: React.ReactNode;
}

// Block Configs
export interface GeneralBlockConfig {
  analytics_location?: string;
  background_color?: string;
  css_classes?: string;
  impression_tracking?: boolean;
  margin?: string;
  padding?: string;
}

export interface ArticleClusterBlockConfig extends GeneralBlockConfig {
  show_bylines?: boolean;
  show_images?: boolean;
  show_timestamps?: boolean;
}

export interface ExpertsBlockConfig extends GeneralBlockConfig {
  experts_headline?: string;
  poster_image?: string;
  video_title?: string;
  video_url?: string;
}

export interface HeroBlockConfig extends GeneralBlockConfig {
  display_price_breakdown?: boolean;
  is_dark_mode?: boolean;
  show_subscribe_button?: boolean;

  // TODO Should update as more variants come along
  // {@link https://app.asana.com/1/1200145886478/project/1208151377480629/task/1211899923220475}
  image_slant?: 'none';
  template_variant?: 'default';
}

export interface HorizontalRuleBlockConfig extends GeneralBlockConfig {
  border_color?: string;
  opacity?: number;
  thickness?: number;
}

export interface ImageBlockConfig extends GeneralBlockConfig {
  alignment?: string;
  lazy_load?: boolean;
  width?: number;
}

export interface QuoteBlockConfig extends GeneralBlockConfig {
  pro_subscription?: boolean;
}

export interface SectionBlockConfig extends GeneralBlockConfig {
  alignment?: keyof typeof alignmentValues;
  background_color?: keyof typeof backgroundColorValues;
  enable_carousel?: boolean;
  font_family?: keyof typeof fontFamilyValues;
  font_size?: keyof typeof fontSizeValues;
  layout?: keyof typeof gridValues;
  margin?: keyof typeof marginValues;
  padding?: keyof typeof paddingValues;
  vertical_alignment?: keyof typeof verticalAlignmentValues;
  wrap?: boolean;
}

export interface SubscribeButtonBlockConfig extends GeneralBlockConfig {
  custom_subscription_description?: string;
  display_subscription_description?: boolean;
  display_price_breakdown?: boolean;
  is_dark_mode?: boolean;
  pro_subscription?: boolean;
  subscribe_one?: boolean;
  text?: string;
}

export interface SubscribeMatrixBlockConfig extends GeneralBlockConfig {
  show_annual?: boolean;
  show_group?: boolean;
  show_monthly?: boolean;
  show_pro?: boolean;
  show_yp?: boolean;
}

export interface TextBlockConfig extends GeneralBlockConfig {
  alignment?: string;
  font_family?: string;
  font_size?: string;
}

export interface VideoBlockConfig extends GeneralBlockConfig {
  autoplay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsinline?: boolean;
  poster?: string;
  preload?: string;
  width?: number;
}
